import { useMemo } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

// ─── Constantes ───────────────────────────────────────────────────────────────

const MESES_ABREV = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

const MESES_ORDEM = {
  janeiro: 1, fevereiro: 2, marco: 3, março: 3, abril: 4,
  maio: 5, junho: 6, julho: 7, agosto: 8,
  setembro: 9, outubro: 10, novembro: 11, dezembro: 12,
};

const PANEL_BG   = "#ffffffff";
const GRID_LINE  = "rgba(255,255,255,0.08)";
const AXIS_COLOR = "#8892b0";
const TITLE_ST   = {
  fontSize: 13, fontWeight: 700, color: AXIS_COLOR,
  textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtMi = (v) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(2)} Mi`;
  if (Math.abs(n) >= 1_000)     return `R$ ${(n / 1_000).toFixed(1)} K`;
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n);
};

const parseFluxo = (val) => {
  if (val == null || val === "") return 0;
  if (typeof val === "number") return val;
  const n = parseFloat(String(val).trim().replace(/\./g, "").replace(",", "."));
  return isNaN(n) ? 0 : n;
};

const normMes = (s) =>
  String(s ?? "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();

// ─── Recharts: dot customizado com label de valor ─────────────────────────────

const RenderDot = ({ cx, cy, value, stroke }) => {
  if (value === null || value === undefined || cx === undefined || cy === undefined) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r={5} fill={stroke} stroke="#0a0f2e" strokeWidth={2} />
      <text
        x={cx} y={cy - 10}
        textAnchor="middle"
        fontSize={10} fontWeight={700} fill={stroke}
      >
        {`${Number(value).toFixed(1)}%`}
      </text>
    </g>
  );
};

// ─── Tooltip customizado ──────────────────────────────────────────────────────

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: PANEL_BG, border: "1px solid #4da6ff",
      borderRadius: 8, padding: "10px 14px",
      fontSize: 12, boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
    }}>
      <div style={{ fontWeight: 700, color: "#fff", marginBottom: 6 }}>{label}</div>
      {payload.map((p) =>
        p.value !== null && (
          <div key={p.name} style={{ color: p.color, fontWeight: 600, marginBottom: 2 }}>
            {p.name}: {`${Number(p.value).toFixed(1)}%`}
          </div>
        )
      )}
    </div>
  );
};

// ─── Visão 1 — Evolução dos Indicadores ──────────────────────────────────────

function EvolucaoIndicadores({ dadosGrafico }) {
  const chartData = useMemo(() => {
    const byLabel = {};
    (dadosGrafico || []).forEach((d) => { byLabel[d.label] = d; });

    return MESES_ABREV.map((mes) => {
      const d = byLabel[mes];
      if (!d || !d.fatLiquido || d.fatLiquido === 0) {
        return { mes, margemBruta: null, margemLiquida: null, ebitdaPct: null };
      }
      return {
        mes,
        margemBruta: d.lucroBruto != null
          ? parseFloat(((d.lucroBruto / d.fatLiquido) * 100).toFixed(2))
          : null,
        margemLiquida: d.resultadoLiq != null
          ? parseFloat(((d.resultadoLiq / d.fatLiquido) * 100).toFixed(2))
          : null,
        ebitdaPct: d.ebitda != null
          ? parseFloat(((d.ebitda / d.fatLiquido) * 100).toFixed(2))
          : null,
      };
    });
  }, [dadosGrafico]);

  return (
    <div style={{ background: PANEL_BG, borderRadius: 12, padding: 20, border: "1px solid rgba(255, 255, 255, 0.08)" }}>
      <div style={TITLE_ST}>Evolução dos Indicadores</div>
      <div style={{ width: "100%", height: 290 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 24, right: 16, bottom: 0, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_LINE} vertical={false} />
            <XAxis
              dataKey="mes"
              stroke={AXIS_COLOR} fontSize={11}
              tickLine={false} axisLine={false} dy={6}
            />
            <YAxis
              stroke={AXIS_COLOR} fontSize={11}
              tickLine={false} axisLine={false}
              tickFormatter={(v) => `${v}%`}
              domain={["auto", "auto"]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="top"
              wrapperStyle={{ fontSize: 11, paddingBottom: 4 }}
              iconType="circle" iconSize={8}
              formatter={(value) => (
                <span style={{ color: "#cbd5e1", fontWeight: 600 }}>{value}</span>
              )}
            />
            <Line
              type="monotone"
              dataKey="margemBruta"
              name="Margem Bruta"
              stroke="#F59E0B"
              strokeWidth={2.5}
              connectNulls={false}
              dot={(props) => <RenderDot {...props} stroke="#F59E0B" />}
              activeDot={{ r: 6, fill: "#F59E0B" }}
            />
            <Line
              type="monotone"
              dataKey="margemLiquida"
              name="Margem Líquida"
              stroke="#16A34A"
              strokeWidth={2.5}
              connectNulls={false}
              dot={(props) => <RenderDot {...props} stroke="#16A34A" />}
              activeDot={{ r: 6, fill: "#16A34A" }}
            />
            <Line
              type="monotone"
              dataKey="ebitdaPct"
              name="EBITDA %"
              stroke="#4da6ff"
              strokeWidth={2.5}
              connectNulls={false}
              dot={(props) => <RenderDot {...props} stroke="#4da6ff" />}
              activeDot={{ r: 6, fill: "#4da6ff" }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ─── Visão 2 — Posição Financeira ────────────────────────────────────────────
//  dadosFluxo: rows do fluxo de caixa já filtrados por ano/mês (vem de VisaoGeral).
//  Caixa           = (=) Resultado da última 5ª Semana do período.
//  Contas a Receber = soma de todas as colunas (+) das 5ª Semanas do período.
//  Contas a Pagar   = soma de todas as colunas (-) das 5ª Semanas do período.

function PosicaoFinanceira({ dadosFluxo = [] }) {
  const posicao = useMemo(() => {
    // Filtra apenas 5ª Semana
    const quintas = dadosFluxo.filter((d) =>
      String(d["Semana"] ?? "").trim().includes("5")
    );

    if (!quintas.length) return { caixa: null, contasReceber: null, contasPagar: null };

    // Caixa: último mês da 5ª Semana (saldo final do período)
    const sorted = [...quintas].sort(
      (a, b) =>
        (MESES_ORDEM[normMes(a["Mês"])] ?? 0) -
        (MESES_ORDEM[normMes(b["Mês"])] ?? 0)
    );
    const ultima = sorted[sorted.length - 1];
    const caixa  = parseFluxo(ultima?.["(=) Resultado"]);

    // Contas a Receber / Pagar: todas as semanas do período filtrado
    let contasReceber = 0;
    let contasPagar   = 0;

    dadosFluxo.forEach((row) => {
      Object.keys(row).forEach((k) => {
        const key = k.trim();
        if (key.startsWith("(+)")) contasReceber += parseFluxo(row[k]);
        if (key.startsWith("(-)")) contasPagar   += parseFluxo(row[k]);
      });
    });

    return { caixa, contasReceber, contasPagar };
  }, [dadosFluxo]);

  const indicadores = [
    {
      label: "Caixa e Equivalentes",
      sub:   "5ª Semana · saldo final do período",
      valor: posicao.caixa,
      cor:   "#16A34A",
    },
    {
      label: "Contas a Receber",
      sub:   "Soma das entradas (+) · todas as semanas",
      valor: posicao.contasReceber,
      cor:   "#4da6ff",
    },
    {
      label: "Contas a Pagar",
      sub:   "Soma das saídas (−) · todas as semanas",
      valor: posicao.contasPagar,
      cor:   "#f5c518",
    },
  ];

  const semDados = posicao.caixa === null;

  return (
    <div style={{
      background: PANEL_BG, borderRadius: 12, padding: 20,
      border: "1px solid rgba(255,255,255,0.08)",
      display: "flex", flexDirection: "column",
    }}>
      <div style={TITLE_ST}>Posição Financeira</div>

      {semDados ? (
        <div style={{
          color: AXIS_COLOR, fontSize: 13, textAlign: "center",
          flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
          minHeight: 120,
        }}>
          Sem dados para o período selecionado.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", marginTop: 8 }}>
          {indicadores.map(({ label, sub, valor, cor }, i) => (
            <div
              key={label}
              style={{
                display: "flex", alignItems: "center", gap: 14,
                padding: "14px 0",
                borderBottom: i < indicadores.length - 1
                  ? "1px solid rgba(255,255,255,0.06)"
                  : "none",
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: AXIS_COLOR, fontWeight: 600 }}>{label}</div>
                <div style={{ fontSize: 10, color: AXIS_COLOR, opacity: 0.6, marginTop: 2 }}>{sub}</div>
              </div>
              <span style={{
                fontSize: 15, fontWeight: 800,
                color: valor === null ? AXIS_COLOR : cor,
              }}>
                {fmtMi(valor)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Export principal ─────────────────────────────────────────────────────────

export function IndicadoresFinanceiros({ dadosGrafico, dadosFluxo = [] }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, width: "100%" }}>
      <EvolucaoIndicadores dadosGrafico={dadosGrafico} />
      <PosicaoFinanceira dadosFluxo={dadosFluxo} />
    </div>
  );
}
